import React, {FC, useCallback, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroupSpacer} from '../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {SameWidthButton} from '../../../../../libs/shared/ui/src/components/buttons/sameWidthButton';
import {useClipboard} from '../../../../helpers/hooks/clipboardHookHelpers';
import {Dialog} from '../../../common/dialogs/dialog';
import {DialogButtonGroup} from '../../../common/dialogs/dialogButtonGroup';
import {DialogHeader} from '../../../common/dialogs/dialogHeader';
import {SideFrameDialogDefinition} from './sideFrameDialogsHooks';

/*
 * Props.
 */

interface SideFrameReportProps {
  reportData: ReportDefinition;
  onRequestClose: Handler;
}

/*
 * Style.
 */

const StyledReportPre = styled.pre`
  display: block;
  overflow: auto;

  padding: 10px;
  margin: 20px;

  background: ${(p) => p.theme.greys.shade10};
  border-radius: 6px;
`;

/*
 * Component.
 */

const SideFrameReport: FC<SideFrameReportProps> = ({reportData, onRequestClose}) => {
  const {title, message} = reportData;
  const {copy} = useClipboard();

  /*
   * Event handlers.
   */

  const onCopyClick = useCallback(() => {
    copy(message);
  }, [copy, message]);

  /*
   * Render.
   */

  return (
    <Dialog width={640} maxHeight="70%" onRequestClose={onRequestClose}>
      <DialogHeader title={title} onRequestClose={onRequestClose} />

      <StyledReportPre>{message}</StyledReportPre>

      <DialogButtonGroup>
        <ButtonGroupSpacer />
        <SameWidthButton onClick={onCopyClick}>
          <Trans id="plugin-report-copy">Copy</Trans>
        </SameWidthButton>
        <SameWidthButton variant={ButtonVariantsEnum.PRIMARY} onClick={onRequestClose}>
          <Trans id="plugin-report-ok">Ok</Trans>
        </SameWidthButton>
      </DialogButtonGroup>
    </Dialog>
  );
};

/*
 * Hook.
 */

interface ReportDefinition extends SideFrameDialogDefinition {
  title: string;
  message: string;
}

export function useSideFrameReport() {
  const [reportData, setReportData] = useState<ReportDefinition | null>(null);
  if (!reportData) {
    return {
      setReportData,
      report: null,
    };
  }

  const onRequestClose = () => {
    if (!reportData) {
      return;
    }

    reportData.onDone();
    setReportData(null);
  };

  return {
    setReportData,
    report: <SideFrameReport reportData={reportData} onRequestClose={onRequestClose} />,
  };
}
